import { type CsticId, FullCsticIdFromResolvedDefinition, SimpleCsticId } from "~/Configurators/Linear/models";
import LabeledInput from "../../../../common/components/LabeledInput";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { type SubstepType } from "~/Configurators/Linear/dtos";
import { IconClickable } from "../../../../common/components/icon/IconClickable";
import { IconType } from "../../../../common/components/icon/IconType";
import { IconSize } from "../../../../common/components/icon/IconSize";
import Tab from "../../../../common/components/Tabs/Tab";

import classNames from "classnames";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/common/m3/Dialog/Dialog";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import { SimpleCsticTileField } from "~/Configurators/Shaft/components/SimpleCsticTileField";
import { LinearTileV2 } from "~/Configurators/Shaft/substeps/LinearTileV2";
import { useCsticSelectProps } from "~/Configurators/Shaft/components/useCsticSelectProps";
import { Stack } from "@mui/material";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";

import { useEffect, useRef } from "react";
import type { ResolvedCsticDefinition } from "~/Configurators/common/v2/StepDefinition";

interface Props {
  className?: string;
  cstic: ResolvedCsticDefinition;
  tabsCstic?: ResolvedCsticDefinition;
  substepType: SubstepType;
}

export const SimpleCsticSelectPopup = ({ cstic: cstic, tabsCstic: tabsCstic, className, substepType }: Props) => {
  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const csticId = FullCsticIdFromResolvedDefinition(cstic);

  const {
    cstic: lubricationConnectorCstic,
    selectProps: { error },
  } = useCsticSelectProps({
    csticId,
    substep: substepType,
    required: cstic.required,
  });

  const {
    selectProps: { onChange: setActiveTab, value: activeTab },
    csticValues: tabs,
  } = useCsticSelectProps({
    csticId: tabsCstic ? FullCsticIdFromResolvedDefinition(tabsCstic) : SimpleCsticId("N/A"),
    substep: substepType,
  });

  const lastActiveTab = useRef<string | null>(activeTab);
  useEffect(() => {
    if (isModalOpen) {
      lastActiveTab.current = activeTab;
    }
  }, [isModalOpen]);

  const onCloseModal = (reason?: "close" | "valueSelected") => {
    closeModal();
    if (reason === "close") {
      setActiveTab(lastActiveTab.current!);
    }
  };

  const selectedConnector = lubricationConnectorCstic.value;
  const selectedValue = lubricationConnectorCstic.values.find((val) => val.value === selectedConnector);
  const selectedValueLabel = selectedValue?.label ?? "";

  const label = lubricationConnectorCstic.label || "MISSING LABEL";

  const spanClasses = classNames("scha__lc__external-label", {
    "f-disabled-text": cstic.disabled,
  });

  return (
    <>
      <div className={className}>
        <LabeledInput
          label={<LanguageAwareExternalContent content={label} loadedClassname={"scha__lc__external-label"} />}
          hasError={error}
        >
          <div className={"title-medium mt-1 d-flex flex-align-center"}>
            {selectedValue?.standard && <LinearTileV2.StandardIndicator omitText className="icon-margin" />}
            <span className={"icon-margin"}>
              <LanguageAwareExternalContent content={selectedValueLabel} loadedClassname={spanClasses} />
            </span>
            <IconClickable
              type={IconType.Pencil}
              size={IconSize.FONT_20}
              onClick={openModal}
              disabled={cstic.disabled}
            />
          </div>
        </LabeledInput>
      </div>
      {isModalOpen && (
        <SelectionModal
          onClose={onCloseModal}
          title={label}
          description={lubricationConnectorCstic.description}
          activeTab={activeTab ?? ""}
          onTabChange={setActiveTab}
          csticId={csticId}
          tabs={tabs}
          substepType={substepType}
        />
      )}
    </>
  );
};

type TabData = {
  value: string;
  label: string;
};

interface SelectionModalProps {
  onClose: (reason?: "close" | "valueSelected") => void;
  title: string;
  description?: string;
  tabs: TabData[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  csticId: CsticId;
  substepType: SubstepType;
}

function SelectionModal({
  onClose,
  title,
  description,
  tabs,
  activeTab,
  onTabChange,
  csticId,
  substepType,
}: SelectionModalProps) {
  const xmlT = useXmlConfiguratorTranslations().t!;
  return (
    <Dialog open={true} onClose={() => onClose("close")} maxWidth="desktop-m">
      <DialogHeader onClose={() => onClose("close")}>
        <DialogTitle sx={{ p: 0 }}>
          <LanguageAwareExternalContent content={title} loadedClassname={"scha__lc__external-label"} />
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack gap={2}>
          {description && (
            <LanguageAwareExternalContent content={description} loadedClassname={"scha__lc__external-label"} />
          )}
          {tabs.length > 0 && (
            <Tabs onChange={(e, val) => onTabChange(val)} variant="fullWidth" value={activeTab}>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  value={tab.value}
                  label={
                    <LanguageAwareExternalContent content={tab.label} loadedClassname={"scha__lc__external-label"} />
                  }
                />
              ))}
            </Tabs>
          )}

          <SimpleCsticTileField
            csticId={csticId}
            tilesPerRow={4}
            substepType={substepType}
            renderTile={({ onClick, disabled, selected, value }) => (
              <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
                <LinearTileV2.Image src={xmlT(value.imageUrl)} ratio="" />
                <LinearTileV2.CheckedIndicator />
                <p className="m-0">{value.value}</p>
                {value.standard && <LinearTileV2.StandardIndicator />}
              </LinearTileV2.Base>
            )}
            onClick={() => onClose("valueSelected")}
          />
        </Stack>
        {/* 
        {tiles.length > 0 && (
          <Grid container columns={4} spacing={"var(--gutter)"}>
            {tiles.map((tile, index) => (
              <Grid key={index} size={1}>
                <LinearTile
                  key={tile.value}
                  index={index}
                  isStandard={tile.standard}
                  imageUrl={tile.imageUrl}
                  onClickHandler={() => {
                    onTileClick(tile);
                  }}
                  info={tile.info}
                  isPicked={tile.value === activeTile}
                  title={tile.label}
                />
              </Grid>
            ))}
          </Grid>
        )} */}
      </DialogContent>
    </Dialog>
  );
}
